import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Toggle SSE logging here. Set to `true` to re-enable logs for debugging.
const SSE_DEBUG = false;
const sseLog = (...args: any[]) => {
  if (SSE_DEBUG) console.log(...args);
};

export interface SSEEvent {
  id?: string;
  type: string;
  data: any;
  timestamp: number;
}

export interface SSEState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  events: SSEEvent[];
  lastEvent: SSEEvent | null;
  connectionRetries: number;
  maxRetries: number;
}

const initialState: SSEState = {
  connected: false,
  connecting: false,
  error: null,
  events: [],
  lastEvent: null,
  connectionRetries: 0,
  maxRetries: 5,
};

const sseSlice = createSlice({
  name: "sse",
  initialState,
  reducers: {
    // Connection management
    connectSSE: (state) => {
      state.connecting = true;
      state.error = null;
    },
    sseConnected: (state) => {
      state.connected = true;
      state.connecting = false;
      state.error = null;
      state.connectionRetries = 0;
    },
    sseDisconnected: (state) => {
      state.connected = false;
      state.connecting = false;
    },
    sseConnectionError: (state, action: PayloadAction<string>) => {
      state.connected = false;
      state.connecting = false;
      state.error = action.payload;
      state.connectionRetries += 1;
    },
    disconnectSSE: (state) => {
      state.connected = false;
      state.connecting = false;
      state.error = null;
    },
    resetConnectionRetries: (state) => {
      state.connectionRetries = 0;
    },

    // Event handling
    sseEventReceived: (state, action: PayloadAction<SSEEvent>) => {
      const event = action.payload;
      sseLog("📡 SSE Event Received:", {
        type: event.type,
        data: event.data,
        id: event.id,
        timestamp: new Date(event.timestamp).toISOString(),
      });
      
      state.lastEvent = event;
      state.events.push(event);
      
      // Keep only last 100 events to prevent memory leaks
      if (state.events.length > 100) {
        state.events = state.events.slice(-100);
      }
    },

    // Event type specific actions
    handleProductUpdate: (state, action: PayloadAction<any>) => {
      sseLog("🔄 Product Update Event:", action.payload);
      // This will be handled by the product saga
    },
    handleCategoryUpdate: (state, action: PayloadAction<any>) => {
      sseLog("📁 Category Update Event:", action.payload);
      // This will be handled by the category saga
    },
    handleOrderUpdate: (state, action: PayloadAction<any>) => {
      sseLog("🛒 Order Update Event:", action.payload);
      // This will be handled by the order saga
    },
    handleInventoryUpdate: (state, action: PayloadAction<any>) => {
      sseLog("📦 Inventory Update Event:", action.payload);
      // This will be handled by the inventory saga
    },
    handleNotification: (state, action: PayloadAction<any>) => {
      sseLog("🔔 Notification Event:", action.payload);
      // Handle real-time notifications
    },
    handleGenericEvent: (state, action: PayloadAction<{ type: string; data: any }>) => {
      sseLog("⚡ Generic Event:", action.payload);
      // Handle any other event types
    },

    // Utility actions
    clearEvents: (state) => {
      state.events = [];
      state.lastEvent = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  connectSSE,
  sseConnected,
  sseDisconnected,
  sseConnectionError,
  disconnectSSE,
  resetConnectionRetries,
  sseEventReceived,
  handleProductUpdate,
  handleCategoryUpdate,
  handleOrderUpdate,
  handleInventoryUpdate,
  handleNotification,
  handleGenericEvent,
  clearEvents,
  clearError,
} = sseSlice.actions;

export default sseSlice.reducer;